import { LECTURE_ROUTES } from "../homeDashboard/lecture/lectureRoutes";
import { qbankTopicSelection, shouldOpenSubtopics } from './qbankNavigation';
import { QUESTION_BANK_ROUTES } from "./questionBankRoutes";
import { EXPLORE_ROUTES } from "../homeDashboard/exploreRoutes";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../QuestionBankSubjects.css";
import fallbackSubjectImage from "../assets/images/battlesubject.jpg";
import { useQuestionBankTopicDetail } from "../hooks/useQuestionBankTopicDetail";
import { useQuestionBankSubTopicDetail } from "../hooks/useQuestionBankSubTopicDetail";
import { buildQuestionBankBackTarget, buildQuestionBankFlowState } from "./qbankFlowState";
import { buildLectureFlowState, persistLectureFlowState } from "../homeDashboard/lecture/lectureFlowState";
import { FLASH_CARD_ROUTES } from "../homeDashboard/flashcard/flashCardRoutes";
import { CONTENT_ROUTES } from "../homeDashboard/contentRoutes";

const NOTES_CATEGORY_ID = 4;
const LECTURES_CATEGORY_ID = 3;
const FLASH_CARDS_CATEGORY_ID = 9;
const PODCAST_CATEGORY_ID = 12;
const INFOGRAPHICS_CATEGORY_ID = 13;

function buildSubTopicRequestParams(requestParams, topicId, navigationState) {
  const categoryId = Number(requestParams?.category_id || 2);

  return {
    state_id: Number(requestParams?.state_id || 0),
    subject_id: Number(requestParams?.subject_id || 0),
    topic_id: Number(topicId || 0),
    category_id: categoryId,
    ...(Number(requestParams?.exam_id || navigationState?.exam_id || navigationState?.exams_id) > 0
      ? { exam_id: Number(requestParams?.exam_id || navigationState?.exam_id || navigationState?.exams_id) } : {}),
    ...(categoryId === FLASH_CARDS_CATEGORY_ID
      ? {
          exam_id: Number(requestParams?.exam_id || navigationState?.exam_id || navigationState?.exams_id || 0),
          free_paid: requestParams?.free_paid || "",
        }
      : {}),
    ...(categoryId === PODCAST_CATEGORY_ID && requestParams?.languages
      ? { languages: requestParams.languages }
      : {}),
  };
}

function buildVisiblePages(currentPage, totalPages) {
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const halfWindow = Math.floor(maxVisiblePages / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - maxVisiblePages + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

function TopicCardShimmer() {
  return (
    <article className="subject-info-card topic-subject qbankStyle" aria-hidden="true">
      <div className="subject-info-card__main">
        <div className="subject-info-card__image">
          <span className="dashboard-skeleton dashboard-skeleton--thumb subject-info-card__shimmer-image" />
        </div>
        <div className="subject-info-card__content">
          <div className="subject-info-card__descriptionWrap">
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title subject-info-card__shimmer-title" />
            <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__shimmer-line" />
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short subject-info-card__shimmer-line subject-info-card__shimmer-line--short" />
          </div>
        </div>
      </div>
      <span className="dashboard-skeleton dashboard-skeleton--button subject-info-card__shimmer-button" />
    </article>
  );
}

const TopicInfoCard = ({ topic, requestParams, navigationState, subtopicsEnabled = false, forceSubtopicFlow = false, mode = "topic" }) => {
  const navigate = useNavigate();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);
  const topicDetailQuery = useQuestionBankTopicDetail(
    {
      ...requestParams,
      topic_id: topic.id,
    },
    {
      enabled: mode === 'topic' && isInfoExpanded && topic.id !== undefined && topic.id !== null,
    }
  );
  const subTopicDetailQuery = useQuestionBankSubTopicDetail(
    {
      ...requestParams,
      sub_topic_id: topic.id,
    },
    {
      enabled: mode === "subtopic" && isInfoExpanded && topic.id !== undefined && topic.id !== null,
    }
  );
  const activeDetailQuery = mode === "subtopic" ? subTopicDetailQuery : topicDetailQuery;
  const topicDetail = activeDetailQuery.data;
  const isTopicDetailLoading = activeDetailQuery.isLoading;
  const hasTopicDetailError = activeDetailQuery.isError;
  const topicDetailError = activeDetailQuery.error;
  const isNotesCategory = Number(requestParams?.category_id || 0) === NOTES_CATEGORY_ID;
  const isLecturesCategory = Number(requestParams?.category_id || 0) === LECTURES_CATEGORY_ID;
  const isFlashCardsCategory = Number(requestParams?.category_id || 0) === FLASH_CARDS_CATEGORY_ID;
  const isPodcastCategory = Number(requestParams?.category_id || 0) === PODCAST_CATEGORY_ID;
  const isInfographicsCategory = Number(requestParams?.category_id || 0) === INFOGRAPHICS_CATEGORY_ID;

  const topicFlowState = buildQuestionBankFlowState({
    ...navigationState,
    subject_id: Number(requestParams?.subject_id || 0),
    state_id: Number(requestParams?.state_id || 0),
    category_id: Number(requestParams?.category_id || 2),
    ...qbankTopicSelection(topic, mode, subtopicsEnabled, forceSubtopicFlow),
  });

  const progressValue = topicDetail?.guess_score || topic.guess_score || "0.00%";
  const progressNumericValue = Number.parseFloat(String(progressValue).replace("%", "")) || 0;
  const childCountValue = Number(
    topicDetail?.child_count ?? topicDetail?.sub_topics_count ?? topic?.child_count ?? topic?.childCount ?? topic?.sub_topics_count ?? topic?.topic_count ?? 0
  );
  const opensSubtopics = mode === 'topic' && shouldOpenSubtopics(topic.id, childCountValue, subtopicsEnabled, forceSubtopicFlow);
  const detailQuestionsValue = `${Number(topicDetail?.attempted_questions ?? topic.attempted_questions ?? 0)}/${Number(topicDetail?.total_questions ?? topic.total_questions ?? 0)}`;
  const detailNotesValue = Number(topicDetail?.total_notes ?? topic?.total_notes ?? 0);
  const detailNotesTimeValue = topicDetail?.total_notes_time || topic?.total_notes_time || "00:00:00";
  const detailFlashCardsValue = Number(topicDetail?.total_flash_cards ?? topic?.total_flash_cards ?? 0);
  const detailReadFlashCardsValue = Number(topicDetail?.total_read_flash_cards ?? topic?.total_read_flash_cards ?? 0);
  const detailFlashCardsTimeValue = topicDetail?.total_time_spent || topicDetail?.total_flash_card_time || topic?.total_time_spent || "00:00:00";
  const detailInfographicsValue = Number(topicDetail?.total_infographics ?? topic?.total_infographics ?? 0);
  const detailReadInfographicsValue = Number(topicDetail?.total_read_infographics ?? topic?.total_read_infographics ?? 0);
  const detailInfographicsTimeValue = topicDetail?.total_time_spent || topic?.total_time_spent || "00:00:00";
  const detailLecturesValue = Number(topicDetail?.total_lectures ?? topic?.total_lectures ?? 0);
  const detailLecturesTimeValue = topicDetail?.total_time_spent || topicDetail?.total_lectures_time || topic?.total_lectures_time || "00:00:00";

  const handleTopicOpen = () => {
    if (mode !== "topic") {
      return;
    }

    navigate(EXPLORE_ROUTES.subtopics, {
      state: buildQuestionBankFlowState({
        ...topicFlowState,
        selectedTopic: topic,
        subTopicRequestParams: buildSubTopicRequestParams(requestParams, topic.id, navigationState),
        qbankBackTarget: buildQuestionBankBackTarget(EXPLORE_ROUTES.topics, navigationState || topicFlowState),
      }),
    });
  };

  const handleQuestionListOpen = () => {
    const listFlowState = buildQuestionBankFlowState({
        ...topicFlowState,
        qbankBackTarget: buildQuestionBankBackTarget(
          mode === "subtopic" ? EXPLORE_ROUTES.subtopics : EXPLORE_ROUTES.topics,
          navigationState || topicFlowState
        ),
      });
    const nextState = isLecturesCategory ? buildLectureFlowState(listFlowState) : listFlowState;
    if (isLecturesCategory) persistLectureFlowState(nextState);
    navigate(isNotesCategory ? CONTENT_ROUTES.notes : isLecturesCategory ? LECTURE_ROUTES.list : isPodcastCategory ? CONTENT_ROUTES.podcasts : isFlashCardsCategory ? FLASH_CARD_ROUTES.start : isInfographicsCategory ? CONTENT_ROUTES.infographics : QUESTION_BANK_ROUTES.list, { state: nextState });
  };

  const handleCardClick = () => {
    if (opensSubtopics) {
      handleTopicOpen();
      return;
    }

    handleQuestionListOpen();
  };

  return (
    <article
      className={`subject-info-card topic-subject qbankStyle${opensSubtopics ? " subject-info-card--clickable" : ""}`}
      onClick={handleCardClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (opensSubtopics) {
            handleTopicOpen();
            return;
          }

          handleQuestionListOpen();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="subject-info-card__main">
        <div className="subject-info-card__image">
          <img src={topic.image_url || fallbackSubjectImage} alt={topic.name || "Topic"} />
        </div>

        <div className="subject-info-card__content">
          <h3 className="subject-info-card__title">{topic.name}</h3>
          <div className="subject-info-card__descriptionWrap">
            <p className={`subject-info-card__description ${isDescriptionExpanded ? "is-expanded" : ""}`}>
              {topic.description || "Description will be available soon."}
            </p>
            <button
              type="button"
              className="subject-info-card__descriptionToggle"
              onClick={(event) => {
                event.stopPropagation();
                setIsDescriptionExpanded((current) => !current);
              }}
              aria-label={
                isDescriptionExpanded
                  ? `Show less description for ${topic.name}`
                  : `Show more description for ${topic.name}`
              }
            >
              {isDescriptionExpanded ? "Show less" : "Show more"}
            </button>
          </div>
        </div>
      </div>

      {!isInfoExpanded && (
        <button
          type="button"
          className="subject-info-card__action"
          onClick={(event) => {
            event.stopPropagation();
            setIsInfoExpanded(true);
          }}
          aria-label={`Get more information about ${topic.name}`}
        >
          <span className="subject-info-card__action-text">
            <i className="bi bi-info-circle" aria-hidden="true"></i>
            Get More Info
          </span>

          <i className="fa-solid fa-chevron-right subject-info-card__arrow" aria-hidden="true" />
        </button>
      )}

      {isInfoExpanded && (
        <div className="subject-info-card__details" aria-label={`${topic.name} details`}>
          {isTopicDetailLoading ? (
            <div className="subject-info-card__detailsShimmer" aria-hidden="true">
              <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__detailsShimmer-bar" />
              <div className="subject-info-card__stats">
                <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__detailsShimmer-stat" />
                <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__detailsShimmer-stat" />
                <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__detailsShimmer-stat" />
              </div>
            </div>
          ) : null}
          {!isTopicDetailLoading && hasTopicDetailError ? (
            <div className="cs-status">
              {topicDetailError?.response?.data?.message || topicDetailError?.message || "Unable to load topic details."}
            </div>
          ) : null}
          {!isTopicDetailLoading && !hasTopicDetailError && isNotesCategory ? (
            <div className="subject-info-card__stats">
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">{mode === "subtopic" ? "Sub Topics" : "Children"}</span>
                <span className="subject-info-card__stat-value">{childCountValue}</span>
              </div>
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Notes</span>
                <span className="subject-info-card__stat-value">{detailNotesValue}</span>
              </div>
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Time</span>
                <span className="subject-info-card__stat-value">{detailNotesTimeValue}</span>
              </div>
            </div>
          ) : null}
          {!isTopicDetailLoading && !hasTopicDetailError && isFlashCardsCategory ? (
            <div className="subject-info-card__stats">
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Flash Cards</span>
                <span className="subject-info-card__stat-value">{detailFlashCardsValue}</span>
              </div>
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Read</span>
                <span className="subject-info-card__stat-value">{detailReadFlashCardsValue}</span>
              </div>
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Time</span>
                <span className="subject-info-card__stat-value">{detailFlashCardsTimeValue}</span>
              </div>
            </div>
          ) : null}
          {!isTopicDetailLoading && !hasTopicDetailError && isInfographicsCategory ? (
            <div className="subject-info-card__stats">
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Infographics</span>
                <span className="subject-info-card__stat-value">{detailInfographicsValue}</span>
              </div>
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Read</span>
                <span className="subject-info-card__stat-value">{detailReadInfographicsValue}</span>
              </div>
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Time</span>
                <span className="subject-info-card__stat-value">{detailInfographicsTimeValue}</span>
              </div>
            </div>
          ) : null}
          {!isTopicDetailLoading && !hasTopicDetailError && isLecturesCategory ? (
            <div className="subject-info-card__stats">
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">{mode === "subtopic" ? "Sub Topics" : "Children"}</span>
                <span className="subject-info-card__stat-value">{childCountValue}</span>
              </div>
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Lectures</span>
                <span className="subject-info-card__stat-value">{detailLecturesValue}</span>
              </div>
              <div className="subject-info-card__stat">
                <span className="subject-info-card__stat-label">Time</span>
                <span className="subject-info-card__stat-value">{detailLecturesTimeValue}</span>
              </div>
            </div>
          ) : null}
          {!isTopicDetailLoading && !hasTopicDetailError && !isNotesCategory && !isFlashCardsCategory && !isInfographicsCategory && !isLecturesCategory ? (
            <>
              <div className="subject-info-card__progressRow">
                <div className="subject-info-card__progressTrack" aria-hidden="true">
                  <div
                    className="subject-info-card__progressFill"
                    style={{ width: `${Math.max(0, Math.min(100, progressNumericValue))}%` }}
                  />
                </div>
                <span className="subject-info-card__progressValue">{progressValue}</span>
              </div>

              <div className="subject-info-card__stats">
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Guess Score</span>
                  <span className="subject-info-card__stat-value">{progressValue}</span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">{mode === "subtopic" ? "Items" : "Children"}</span>
                  <span className="subject-info-card__stat-value">{childCountValue}</span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Questions</span>
                  <span className="subject-info-card__stat-value">{detailQuestionsValue}</span>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}
    </article>
  );
};

const TopicCard = ({
  topics = [],
  isLoading = false,
  requestParams = {},
  subtopicsEnabled = false,
  forceSubtopicFlow = false,
  mode = "topic",
  pagination = {},
  currentPage = 1,
  onPageChange,
  emptyMessage,
  navigationState,
}) => {
  const normalizedTopics = useMemo(
    () =>
      topics.map((topic) => ({
        ...topic,
        id: Number(topic?.id),
      })),
    [topics]
  );
  const totalPages = Number(pagination?.totalPages || pagination?.total_pages || 1);
  const visiblePages = buildVisiblePages(currentPage, totalPages);

  if (isLoading) {
    return (
      <section className="subject-info-section" aria-label="Topic information">
        <div className="subject-info-grid">
          {Array.from({ length: 4 }).map((_, index) => (
            <TopicCardShimmer key={index} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="subject-info-section" aria-label="Topic information">
      {normalizedTopics.length ? (
        <div className="subject-info-grid">
          {normalizedTopics.map((topic) => (
            <TopicInfoCard
              key={topic.id}
              topic={topic}
              requestParams={requestParams}
              navigationState={navigationState}
              subtopicsEnabled={subtopicsEnabled}
              forceSubtopicFlow={forceSubtopicFlow}
              mode={mode}
            />
          ))}
        </div>
      ) : (
        <div className="cs-status">{emptyMessage || `No ${mode === "subtopic" ? "sub topics" : "topics"} available.`}</div>
      )}
      {!isLoading && totalPages > 1 ? (
        <div className="subject-info-pagination">
          <button
            type="button"
            className="subject-info-pagination__nav"
            disabled={currentPage <= 1}
            onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
          >
            Previous
          </button>
          <div className="subject-info-pagination__pages">
            {visiblePages.map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={`subject-info-pagination__page${pageNumber === currentPage ? " is-active" : ""}`}
                onClick={() => onPageChange?.(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="subject-info-pagination__nav"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default TopicCard;

